import { useAuthContext } from 'contexts';
import moment from 'moment';
import { useState } from 'react';
import { fhirGet } from 'services';
import useSWR from 'swr';
import type { SWRConfiguration } from 'swr';
import type { CommunicationData } from 'types';
import { CommunicationHelper, defaultSWROptions } from 'utils';

export const useGetUnknownCallerCommunications = (archived) => {
  const { token } = useAuthContext();
  const [communicationsData, setCommunicationsData] = useState<CommunicationData>();

  /* 
  we need to group similar queries together, because if we append them separately,
  the GCP request will only recognize the latest one.
  so instead of identifier=A&identifier=B, we need to do identifier=A,B.
  However, this will return OR matches, not AND matches,
  so we need to do extra filtering after fetch.
  */

  const queries = {
    'identifier': ['https://projectwell.io/fhir/identifiers/communication/unknown-caller|true'],
    'identifier:not': [
      'https://projectwell.io/fhir/identifiers/communication/voicemail|true',
      'https://projectwell.io/fhir/identifiers/communication/extension-line|true',
      'https://projectwell.io/fhir/identifiers/communication/abandoned|true'
    ],
    'medium': ['zoom'], //use medium codes
  }
  const archivedQuery = 'https://projectwell.io/fhir/identifiers/communication/contact-state|deleted'
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- upstream uses a ternary for its side effect; a statement form would change the emit
  archived
    ? queries['identifier'].push(archivedQuery)
    : queries['identifier:not'].push(archivedQuery)

  const query = Object.keys(queries).map(q => q + '=' + (queries as Record<string, string[]>)[q]!.join(',')).join('&')

  const url = `/Communication/?${query}`;

  const swrOptions: SWRConfiguration = {
    //refreshInterval: 60000, //turned off per ruth's request 2025
    ...defaultSWROptions,
  };

  const {
    //data: communicationsData,
    error: communicationsError,
    isValidating: isLoading,
    mutate: communicationsMutate,
    // `fhirGet` resolves `CommunicationData | undefined` when the key is not ready,
    // while swr 0.5.7's fetcher parameter declares no `undefined`.
  } = useSWR<CommunicationData, Error>([url, token], fhirGet as () => Promise<CommunicationData>, {
    ...swrOptions,
    onSuccess: (data) => {
      if (data?.entry?.length > 0) {
        const newDataEntry = data.entry
          .filter(c => {
            // set up commHelper to check for extension/identifier values needed later
            const communicationHelper = new CommunicationHelper(c.resource)
            const extensionLineId = communicationHelper.getIdentifierByCode('https://projectwell.io/fhir/identifiers/communication/extension-line') === 'true'
            const unknownCallerId = communicationHelper.getIdentifierByCode('https://projectwell.io/fhir/identifiers/communication/unknown-caller') === 'true'
            const voicemailId = communicationHelper.getIdentifierByCode('https://projectwell.io/fhir/identifiers/communication/voicemail') === 'true'
            const abandonedId = communicationHelper.getIdentifierByCode('https://projectwell.io/fhir/identifiers/communication/abandoned') === 'true'
            const archivedId = communicationHelper.getIdentifierByCode('https://projectwell.io/fhir/identifiers/communication/contact-state') === 'deleted'
            // return logic
            return archived
              ? archivedId // if archived, just check for archived identifier
              : (unknownCallerId && !archivedId && !extensionLineId && !voicemailId && !abandonedId) // only include unknownCaller, exclude archived, transfer, abandoned & voicemail
          })
          .sort((a, b) => {
            const dateA = moment(a.resource?.sent || a.resource?.received);
            const dateB = moment(b.resource?.sent || b.resource?.received);
            return dateB.valueOf() - dateA.valueOf();
          });
        data.entry = newDataEntry
      }
      setCommunicationsData(data)
    },
  });

  return {
    communicationsData,
    communicationsError,
    communicationsMutate,
    isLoading,
  };
};
